import { AppContext } from 'next/app';
import nextCookies from 'next-cookies';
import uaUtils, { UAParsedMap } from 'ava-util/dist/user-agent';
import { generateAccessToken } from 'base/utils/randomGenerator';
import { getQueryFromPath } from 'base/utils/getQueryFromPath';
import { filterFields } from 'ava-util/dist/data';
import { pages } from 'pro/config/pages';
import { isApp as checkIsApp } from 'base/utils/app';
import {
    COOKIE_LOCALE,
    COOKIE_LANG,
    COOKIE_COUNTRY,
    COOKIE_CURRENCY,
    COOKIE_ACCESS_TOKEN,
    COOKIE_LOGIN_TOKEN,
    COOKIE_IS_GUEST_USER,
    COOKIE_LAST_ORDER_ID,
    COOKIE_CART_SELECTED_IDS,
    COOKIE_VARIANT_NUMS_ITEMS,
    COOKIE_DEBUG_TRANS,
    COOKIE_BUY_NOW_DATA,
    QUERY_COUNTRY,
    QUERY_CURRENCY,
    QUERY_ACCESS_TOKEN,
    QUERY_LOGIN_TOKEN,
    QUERY_LANGUAGE,
    DEBUG_APP,
    DEBUG_TRANS,
} from '../config/constant';
import { COOKIE_AGE_GATE } from 'mod/age-gate/constant';
import { getPageLayout } from 'base/utils/request';

export type PageConfType = {
    viewType?: 'index' | 'list' | 'detail' | 'search' | 'cart' | 'checkout' | 'checkout_option' | 'purchase' | 'order_status' | 'activity' | 'article' | 'blog' | 'gift' | 'page' | 'error' | 'password' | 'lists' | 'customers/login' | 'customers/account' | 'customers/address' | 'customers/order' | 'customers/activate_account' | 'customers/reset_password' | 'customers/register' | 'card' | 'pay_failed' | 'help' | 'help_center' | 'marketing' | 'brand' | 'customers/coupons' | 'customers/orders' | 'refund' | 'customers/order_review' | 'other' | 'diy_pool' | 'customers/packages' | 'customers/track' | 'customers/points' | 'customers/profile' | 'customers/message_center' | 'technical_guide' | 'guides' | 'brochure' | 'customers/other' | 'customers/change_email';
    detail?: string;
    seoCode?: string;
    seoQuery?: Record<string, any>;
    queryKeys?: string[];
    needAuth?: boolean | string[]; // 数组为 query 中需要登录的参数
    i18nNamespace?: string;
    pageCode?: string;
    bodyClassName?: string;
    layoutProps?: Record<string, any>;
    delayPageLoading?: boolean;
    showOpeningPopup?: boolean;
};

export type IParsedAppContext = PageConfType & {
    host: string;
    asPath: string;
    query: Record<string, any>,
    envName: string;
    ua: UAParsedMap & {
        isApp: boolean;
    };
    cookies: Record<string, string | undefined>,
    lang: string;
    country: string;
    currency: string;
    accessToken: string;
    loginToken: string;
    pageLayout: 'default' | 'checkout';
    debugTrans: '0' | '1' | '2';
}

// 判断是否强制登录
const checkNeedAuth = (needAuth: boolean | string[] | undefined, query: Record<string, any>): boolean => {
    let forceLogin = false
    if (Array.isArray(needAuth)) {
        for (let key of needAuth) {
            if (query[key]) {
                forceLogin = true
                break
            }
        }
    } else {
        forceLogin = !!needAuth
    }
    return forceLogin
}

const getPageConf = (cmptPageConf: PageConfType): PageConfType => {
    return {
        pageCode: '',
        bodyClassName: '',
        layoutProps: {},
        delayPageLoading: false,
        showOpeningPopup: true,
        ...cmptPageConf,
        ...pages[cmptPageConf?.pageCode || '']
    };
}

function getUrlLocale(url: string, ctxLocale: string): string {
    url = url.replace(/\?.*$/, '').replace(/#.*$/, '');
    // /en
    if (/^\/[a-zA-Z]{2}(-[a-zA-Z]{2}){0,1}$/.test(url)) {
        return url.replace(/\//g, '');
    }

    const match = url.match(/^\/[a-zA-Z]{2}(-[a-zA-Z]{2}){0,1}\//);
    if (match) {
        const urlLocale = match[0].replace(/\//g, '');
        if (urlLocale.toLowerCase() === ctxLocale.toLowerCase()) {
            return urlLocale;
        }
    }
    return '';
}

function transLocale(locale: string): { lang: string, country: string } {
    let lang = '';
    let country = '';
    if (locale) {
        if (locale.length === 2) {
            lang = locale
        } else if (locale.length === 5) {
            lang = locale.substring(0, 2);
            country = locale.substring(3, 5).toUpperCase();
        }
    }
    return {
        lang: lang.toLowerCase(),
        country: country.toUpperCase()
    }
}

export function parseAppContext(appContext: AppContext): IParsedAppContext {
    const { ctx, Component } = appContext;

    const {
        req,
        asPath = '/',
        query: ctxQuery
    } = ctx;

    const parsedCookies = nextCookies(ctx) || {} as Record<string, any>;
    if (!parsedCookies[COOKIE_ACCESS_TOKEN]) {
        parsedCookies[COOKIE_ACCESS_TOKEN] = generateAccessToken();
    }

    const query = {
        ...ctxQuery,
        ...getQueryFromPath(ctxQuery.path as string)
    };

    const host = (req ? req.headers.host : window.location.host) || '';
    const rawUserAgent = (req ? req.headers['user-agent'] : navigator.userAgent) || '';
    const parsedUa = uaUtils.parse(rawUserAgent);
    const isPhone = process.env.NEXT_PUBLIC_TERMINAL === 'm';
    const isApp = checkIsApp(rawUserAgent) || query[DEBUG_APP] === '1';

    // @ts-ignore
    const locale = getUrlLocale(req?.originalUrl || '', ctx.locale || '') || parsedCookies[COOKIE_LOCALE] || '';
    const localeObj = transLocale(locale);
    let lang = localeObj.lang || query[QUERY_LANGUAGE] as string || parsedCookies[COOKIE_LANG] || '';
    let country = localeObj.country || query[QUERY_COUNTRY] as string || parsedCookies[COOKIE_COUNTRY] || '';
    let currency = query[QUERY_CURRENCY] as string || parsedCookies[COOKIE_CURRENCY] || '';

    // console.log('parseAppContext', {lang, country, currency})

    // @ts-ignore
    const pageConf = getPageConf(Component.pageConf);

    return {
        host,
        asPath,
        envName: process.env.NEXT_PUBLIC_ENV_NAME || 'prod',
        ua: {
            ...parsedUa,
            isPhone,
            isMobile: isPhone || parsedUa.isMobile,
            isApp
        },
        query,
        cookies: filterFields(parsedCookies, [
            COOKIE_LANG,
            COOKIE_COUNTRY,
            COOKIE_CURRENCY,
            COOKIE_ACCESS_TOKEN,
            COOKIE_LOGIN_TOKEN,
            COOKIE_IS_GUEST_USER,
            COOKIE_LAST_ORDER_ID,
            COOKIE_VARIANT_NUMS_ITEMS,
            COOKIE_CART_SELECTED_IDS,
            COOKIE_BUY_NOW_DATA,
            `${COOKIE_AGE_GATE}_${country}`
        ]), // 排除三方cookie
        lang,
        country,
        currency,
        accessToken: query[QUERY_ACCESS_TOKEN] as string || parsedCookies[COOKIE_ACCESS_TOKEN] || '',
        loginToken: query[QUERY_LOGIN_TOKEN] as string || parsedCookies[COOKIE_LOGIN_TOKEN] || '',
        pageLayout: getPageLayout(asPath),
        ...pageConf,
        needAuth: checkNeedAuth(pageConf.needAuth, query),
        viewType: pageConf.viewType === 'purchase' ? (query.success === '0' && query.type !== 'paypal' ? 'pay_failed' : 'purchase') : (pageConf.viewType || 'other'),
        debugTrans: query[DEBUG_TRANS] as IParsedAppContext['debugTrans'] || parsedCookies[COOKIE_DEBUG_TRANS] || '0',
    }
}